import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';
import {
  getNavigationSectionsForRole,
  type NavItem,
} from '@/lib/config/navigation';
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  role: string | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** Mobile drawer open state — separate from desktop collapse. */
  mobileOpen: boolean;
  onMobileClose: () => void;
}

// Vertical nav for the authenticated app. Sectioned by `getNavigationSectionsForRole`.
// Below md (768 px) it acts as a drawer overlay; above md it's a fixed left column.
export function Sidebar({
  role,
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const pathname = useLocation().pathname;
  const sections = role ? getNavigationSectionsForRole(role, pathname) : [];

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={onMobileClose}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-primary text-secondary transition-all duration-200',
          // mobile: slide in from -100% to 0
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // desktop: always visible, width changes with collapsed
          'md:translate-x-0',
          collapsed ? 'md:w-16' : 'md:w-60',
          'w-60', // mobile width
        )}
        aria-label="Primary navigation"
      >
        {/* Brand row */}
        <div
          className={cn(
            'flex items-center h-16 border-b border-primary-700/50',
            collapsed ? 'md:justify-center md:px-0' : 'px-4',
            'justify-between px-4',
          )}
        >
          <Link
            to="/"
            className={cn('flex items-center gap-2', collapsed && 'md:gap-0')}
          >
            <img
              src="/logo.jpg"
              alt="CleanYarn"
              className="h-8 w-8 rounded object-cover"
            />
            {!collapsed && (
              <span className="font-display text-h3 leading-none">CleanYarn</span>
            )}
          </Link>
          <button
            type="button"
            onClick={onMobileClose}
            className="md:hidden p-1 rounded hover:bg-primary-700"
            aria-label="Close navigation"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Nav sections — scrollable region */}
        <nav className="flex-1 overflow-y-auto py-4">
          {sections.map((section) => (
            <div key={section.section} className="mb-4">
              {!collapsed && (
                <p className="px-4 text-tiny uppercase tracking-wider text-secondary/60 mb-1">
                  {section.section}
                </p>
              )}
              <ul>
                {section.items.map((item) => (
                  <SidebarLink key={item.href} item={item} collapsed={collapsed} />
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse toggle (desktop only) */}
        <button
          type="button"
          onClick={onToggleCollapse}
          className="hidden md:flex items-center justify-center h-10 border-t border-primary-700/50 hover:bg-primary-700 text-secondary/70"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronDoubleRightIcon className="h-4 w-4" />
          ) : (
            <ChevronDoubleLeftIcon className="h-4 w-4" />
          )}
        </button>
      </aside>
    </>
  );
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <li>
      <Link
        to={item.href}
        className={cn(
          'flex items-center px-4 py-2 text-small transition-colors',
          item.active
            ? 'bg-primary-800 text-secondary border-l-4 border-secondary'
            : 'border-l-4 border-transparent text-secondary/80 hover:bg-primary-700 hover:text-secondary',
          collapsed && 'md:justify-center md:px-0',
        )}
        title={collapsed ? item.label : undefined}
      >
        <span className={cn(collapsed && 'md:hidden')}>{item.label}</span>
        {collapsed && (
          <span className="hidden md:inline text-tiny font-bold">
            {item.label.charAt(0)}
          </span>
        )}
      </Link>
    </li>
  );
}

// Hamburger trigger for the mobile drawer. Lives in the TopBar, but exported
// here so SidebarOpen state can stay co-located with the sidebar component.
export function MobileNavTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="md:hidden p-2 rounded hover:bg-gray-100"
      aria-label="Open navigation"
    >
      <Bars3Icon className="h-6 w-6 text-gray-700" />
    </button>
  );
}

// Tiny hook so the AppShell can manage both states together.
export function useSidebarState() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  return {
    collapsed,
    mobileOpen,
    toggleCollapse: () => setCollapsed((c) => !c),
    openMobile: () => setMobileOpen(true),
    closeMobile: () => setMobileOpen(false),
  };
}
